import React, { forwardRef, type KeyboardEventHandler } from 'react';
import cx from 'classnames';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import Select from '../Select/Select';
import { TranslateSelect } from '../Select/TranslateSelect';
import stylesheet from './PageHeaderSelect.css';

const renderSelectedValue = (
  selected:
    | { children?: React.ReactNode; value: string }
    | { children?: React.ReactNode; value: string }[],
  placeholder: React.ReactNode,
) => {
  if (Array.isArray(selected)) {
    const { multipleSelected } = TranslateSelect(selected.length);
    return selected.length > 1
      ? multipleSelected
      : selected.length === 1
      ? selected.map((selection) => selection.value)
      : placeholder;
  }
  return selected.value || placeholder;
};

type PageHeaderSelectPropsBase = {
  /**
   * Optional badge component to display next to the select
   */
  badge?: React.ReactNode;
  /**
   *  Link for the call to action
   *  This prop is only relevant in listbox mode
   */
  callToActionHref?: string;
  /**
   *  Label for the call to action at the bottom of the open menu
   *  This prop is only relevant in listbox mode
   */
  callToActionLabel?: string;
  /**
   *  Click handler for the call to action at the bottom of the open menu
   *  This prop is only relevant in listbox mode
   */
  callToActionOnClick?: () => void;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  label: string;
  /** When true, the dropdown menu matches the trigger width. */
  matchTargetWidth?: boolean;
  /** Popover offset from the trigger (listbox). */
  offset?: number;
  /** Used with searchable listbox triggers. */
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  /** Triggers when the listbox is opened. */
  onOpen?: () => void;
  /** Filter options while searching (listbox). */
  optionsFilter?: (value: string | undefined, option: string) => boolean;
  /**
   * Placement of the dropdown menu. Defaults to 'bottom-start'.
   */
  placement?:
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end';
  /**
   * Override collapsed selected display (listbox). When omitted, a default renderer is used.
   */
  renderSelectedValue?: (
    selected:
      | { children?: React.ReactNode; value: string }
      | { children?: React.ReactNode; value: string }[],
    placeholder: React.ReactNode,
  ) => React.ReactNode;
  /** When true, the trigger is an input used to filter options (listbox). */
  searchable?: boolean;
  /**
   * Option to control the width of the select menu. Defaults to `regular`.
   */
  size?: 'small' | 'medium' | 'large' | undefined;
};

export type PageHeaderSelectProps = PageHeaderSelectPropsBase &
  (
    | {
        multiple?: false;
        onChange: (value: string) => void;
        value: string;
      }
    | {
        multiple: true;
        onChange: (value: string[]) => void;
        value: string[];
      }
  );

const PageHeaderSelect = forwardRef<HTMLElement, PageHeaderSelectProps>(
  function (
    {
      children,
      className,
      callToActionLabel,
      callToActionOnClick,
      callToActionHref,
      badge,
      size = 'medium',
      placement = 'bottom-start',
      matchTargetWidth = false,
      multiple = false,
      onChange,
      ...props
    },
    forwardedRef,
  ) {
    const classes = cx(stylesheet.root, className, {
      [stylesheet.smallMenu]: size === 'small',
    });

    return (
      <div className={stylesheet.menuBadgeContainer}>
        <Select
          callToActionOnClick={callToActionOnClick}
          callToActionHref={callToActionHref}
          callToActionLabel={callToActionLabel}
          mode="listbox"
          matchTargetWidth={matchTargetWidth}
          className={classes}
          multiple={multiple}
          renderSelectedValue={renderSelectedValue}
          onChange={(value) => {
            if (multiple) {
              (onChange as (v: string[]) => void)(value as string[]);
            } else {
              (onChange as (v: string) => void)(value as string);
            }
          }}
          ref={forwardedRef}
          size={size}
          placement={
            mcdsFlagCheck('xp_mcds_redesign_bug_fixes_G')
              ? placement
              : undefined
          }
          {...props}
        >
          {children}
        </Select>
        <div className={stylesheet.badge}>{badge}</div>
      </div>
    );
  },
);

export default PageHeaderSelect;
